import React from "react";
import { formatMoney } from "accounting-js";
import { MAX_QUANTITY, PRODUCT_IMAGE_BASE_URL } from "../constants";
import style from "./ProductSelection.module.css";
import { Badge } from "react-bootstrap";
import QuantityInput from "./QuantityInput";
import { clamp } from "lodash-es";
import Image from "./shared/Image";
import {connect} from "react-redux";
import { skusUnderWarranty } from "../redux/selectors";
import BackorderFlag from "./BackorderFlag";

function ProductSelection({
  handleQtyChange,
  handleSelectionClick,
  isActive,
  selection,
  type,
  linkedParts,
  skusUnderWarranty
}) {
  const product = selection.product;
  const isUserDefined =
    selection.selection_can_change_qty === "1" &&
    ["radio", "select", "checkbox"].includes(type);
  const hasPrice = product.price !== null;
  // The selection price would include the price of linked parts as well.
  // See https://canonau.atlassian.net/browse/M2-573

  const currentSelection = selection;
  const price = hasPrice
    ? linkedParts.reduce(
      (price, { selection }) =>
        // One linked part can be linked with multiple products, its qty is the total qty of all linking products, so it is not reliable.
        // Qty of linked part selection should always be the same as the qty of the given product
        // As a consequence, the price should multiply by the qty of current product,
        price + Number(selection.product.price) * currentSelection.selection_qty,
        Number(currentSelection.product.price) * currentSelection.selection_qty
      )
    : null;
  const isEnabled = product.status === "1";
  const isDefault = selection.is_default === "1";
  const canAddToCart = isEnabled && hasPrice;

  let errorMessage = "";
  if (!isEnabled) {
    if (selection.includes_main_unit === "1") {
      errorMessage = "The main unit is disabled.";
    } else if (isDefault) {
      errorMessage = "The default product is disabled.";
    } else {
      errorMessage = "The product is disabled.";
    }
  } else if (!hasPrice) {
    if (selection.includes_main_unit === "1") {
      errorMessage =
        "The main unit is not associated with the current price list.";
    } else if (isDefault) {
      errorMessage =
        "The default product is not associated with the current price list.";
    } else {
      errorMessage =
        "The product is not associated with the current price list.";
    }
  }

  const linkedProducts = linkedParts.map(part => part.selection.product);
  const disabledLinkedProducts = linkedProducts.filter(
    product => product.status === "2"
  );
  const linkedProductsWithoutPrice = linkedProducts.filter(
    product => product.price === null
  );

  if (disabledLinkedProducts.length) {
    errorMessage = `${errorMessage} The linked product(s) ${disabledLinkedProducts
      .map(product => product.sku)
      .join(", ")} is/are disabled.`;
  }
  if (linkedProductsWithoutPrice.length) {
    errorMessage = `${errorMessage} The linked product(s) ${linkedProductsWithoutPrice
      .map(product => product.sku)
      .join(", ")} is/are not associated with the current price group.`;
  }

  const isUnderWarranty = skusUnderWarranty.includes(product.sku);

  return hasPrice ? (
    <tr
      className={`${style.root} ${isActive ? style.active : ""} ${
        canAddToCart ? "" : style.disabled
      }`}
      onClick={() =>
        handleSelectionClick(selection, isActive ? "remove" : "add")
      }
    >
      {/* the radio button/checkbox */}
      <td className={style.itemSelectContainer}>
        <input
          type={["checkbox", "multi"].includes(type) ? "checkbox" : "radio"}
          checked={isActive}
          className={style.itemControl}
          readOnly
        />
      </td>

      {/* product title */}
      <td className={style.itemDescription}>
        <p className={style.title}>
          {product.name}{" "}
          {selection.is_software && <Badge variant="danger">Software</Badge>}
          {isUnderWarranty && <Badge variant="success">Warranty</Badge>}
        </p>

        <p className={style.details}>
          <span className="text-muted small">{product.short_description}</span>

          {linkedProducts.length > 0 && (
            <span className="text-warning small">
              The price includes additional linked parts:
              {linkedProducts.map(product => product.sku).join(", ")}
            </span>
          )}

          {errorMessage && (
            <span className="text-danger small">
              {errorMessage} Therefore the product(s) will not be added into the
              quote. Please contact the Product Marketing Team.
            </span>
          )}
        </p>
      </td>

      {/* product thumbnail */}
      <td>
        {product.small_image ? (
          <Image
            src={PRODUCT_IMAGE_BASE_URL + product.small_image}
            alt="product"
          />
        ) : null}
      </td>

      {/* quantity */}
      <td className={style.selectionQtyCell}>
        {isUserDefined ? (
          <React.Fragment>
            ×
            <QuantityInput
              type="number"
              className={style.selectionQty}
              value={selection.selection_qty}
              onConfirm={e =>
                handleQtyChange(
                  selection.selection_id,
                  clamp(Math.round(e.target.value), 1, MAX_QUANTITY) ||
                    selection.selection_qty
                )
              }
              onClick={e => e.stopPropagation()}
            />
          </React.Fragment>
        ) : null}
      </td>

      {/*Backorder Flag*/}
      <td className={style.itemDisplayPriceContainer}>
        <BackorderFlag quantity={product.inventory_qty} />
      </td>

      {/* price */}
      <td className={style.itemDisplayPriceContainer}>
        {price !== null ? formatMoney(price) : "N/A"}
      </td>
    </tr>
  ) : null;
}


export default connect( state => ({
  skusUnderWarranty: skusUnderWarranty(state)
}))(ProductSelection);
